import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ElementUtils } from "abweb/utils/ElementUtils";

/** Static utility methods that relate to CSS constructs.
 * Some are spreadable style objects, for easy combining with other style objects.
 * eg
 *    <div style={{
 *      ...CssUtils.thinBlackBorder,
 *      ...CssUtils.size100percent,
 *    }}>
 *
 */
export class CssUtils {
  /** Utility method for combining sets of optional strings,
   * and produce a single string for a react component's className prop.
   * Optional strings may be undefined, null, or the value false.
   * eg:
   * composeClassNames("ab-fasst-outer-div",
   * this.props.className, this.props.hasTestData && "ab-fasst-has-test-data");
   */
  static composeClassNames(...values) {
    let keepers = [];
    for (let e of values) {
      if (e != null && e !== false) {
        keepers.push(e);
      }
    }
    return keepers.join(" ");
  }

  /**
   * spreadable style object, for easy combining with other style objects.
   */

  /**
   * spreadable style object, for easy combining with other style objects.
   */
  static height100Minus(minusPx) {
    let s = {};
    s.height = `calc(100% - ${minusPx}px)`;
    return s;
  }

  /**
   * spreadable style object, for easy combining with other style objects.
   */
  static maxHeightPx(numpx) {
    let s = {};
    s.maxHeight = `${numpx}px`;
    return s;
  }

  /**
   * spreadable style object, for easy combining with other style objects.
   */
  static fixedSizePx(widthPx, heightPx) {
    let s = {};
    s.width = `${widthPx}px`;
    s.height = `${heightPx}px`;
    return s;
  }

  /**
   * spreadable style object, for easy combining with other style objects.
   */
  static topLeftPx(topPx, leftPx) {
    let s = {};
    s.top = `${topPx}px`;
    s.left = `${leftPx}px`;
    return s;
  }

  /**
   * spreadable style object, for easy combining with other style objects.
   */
  static relativeTopLeftPx(topPx, leftPx) {
    let s = {};
    s.position = "relative";
    s.top = `${topPx}px`;
    s.left = `${leftPx}px`;
    return s;
  }

  /**
   * spreadable style object, for easy combining with other style objects.
   */
  static minWidthHeightPx(minWidthPx, minHeightPx) {
    let s = {};
    s.minWidth = `${minWidthPx}px`;
    s.minHeight = `${minHeightPx}px`;
    return s;
  }

  /**
   * spreadable style object, for easy combining with other style objects.
   */
  static marginPx(marginPx) {
    let s = {};
    s.margin = `${marginPx}px`;
    return s;
  }

  /**
   * spreadable style object, for easy combining with other style objects.
   */
  static fadeOutTransition(fadeDurationMsecs) {
    let s = {};
    s.opacity = 0;
    s.transition = `opacity ${fadeDurationMsecs}ms ease-in`;
    /* Things that fade out can't get clicks -- else when the item is faded, it will still capture clicks */
    s.pointerEvents = "none";
    return s;
  }

  /**
   * spreadable style object, for easy combining with other style objects.
   */

  /**
   * compose a CSSProperties object that will set a css variable on a react element,
   * which requires special syntax in typescript.
   * Spreading the return value allows it to be combined with other style objects.
   */
  static setVariable(varName, value) {
    // have to pass variable name through react using an any cast on variable name,
    // eg   ["--ab-gexp-line-height-multiplier-private" as any]: 3
    let s = {
      [varName]: value
    };
    return s;
  }

  /**
   * Finds all properties in a style that use <code>calc()</code> and convert
   * any nested <code>calc()</code> calls to plain parenthesis expressions. This simplifies the
   * expressions, and, more importantly, makes the expressions work in older browsers
   * such as Edge 15.
   *
   * @param style     the style object to clean
   */
  static convertNestedCalcsToExpressions(style) {
    Object.getOwnPropertyNames(style).forEach(prop => {
      const value = style[prop];
      if (value.startsWith("calc(")) {
        const newValue = `calc(${value.replace(/calc/g, "")})`;
        style[prop] = newValue;
      }
    });
  }

  /**
   * Converts a given TS property name of a CSS style object to the true CSS value. E.G.: maxWidth => max-width
   * @param tsProperty
   */
  static tsPropertyToCssProperty(tsProperty) {
    return tsProperty.replace(/([A-Z])/g, s => `-${s.toLowerCase()}`);
  }
  static stringOrNumberToCssString(value) {
    return typeof value === "number" ? `${value}px` : value;
  }

  /**
   * These are temporary utilities to check the DOM for which font we are using
   * At some point we will make the server the system of record for fonts. At that point we can delete these utilities.
   */
  static documentFontFamily() {
    const fontFamilyRaw = ElementUtils.getCss("font-family", "Inter", document.documentElement);
    return fontFamilyRaw.split(',')[0].trim();
  }
  static documentFontSize() {
    return ElementUtils.getCssAsNumber("font-size", 16, document.documentElement);
  }
}
_defineProperty(CssUtils, "size100percent", {
  width: "100%",
  height: "100%"
});
/**
 * spreadable style object, for easy combining with other style objects.
 */
_defineProperty(CssUtils, "width100", {
  width: "100%"
});
_defineProperty(CssUtils, "thinBlackBorder", {
  borderColor: "black",
  borderStyle: "solid",
  borderWidth: "thin"
});
/**
 * spreadable style object, for easy combining with other style objects.
 * This background color helps to make a div visible for debugging.
 */
_defineProperty(CssUtils, "backgroundLightCoral", {
  //   This background color helps to make a div visible for debugging.
  backgroundColor: "lightcoral"
});
/**
 * spreadable style object, for easy combining with other style objects.
 * This places a resize handle on the div element,
 * and also provides the overflow:auto which is required for that to work.
 * https://www.w3schools.com/cssref/css3_pr_resize.asp
 */
_defineProperty(CssUtils, "resizableAutoScroll", {
  resize: "both",
  overflow: "auto" // auto: If overflow is clipped, a scroll-bar should be added to see the rest of the content
});
/**
 * spreadable style object, for easy combining with other style objects.
 * This places a resize handle on the div element,
 * and also provides the overflow:auto which is required for that to work.
 * https://www.w3schools.com/cssref/css3_pr_resize.asp
 */
_defineProperty(CssUtils, "resizable", {
  resize: "both"
});
/**
 * spreadable style object, for easy combining with other style objects.
 */
_defineProperty(CssUtils, "overflowVisible", {
  overflow: "visible"
});
/**
 * spreadable style object, for easy combining with other style objects.
 */
_defineProperty(CssUtils, "overflowAutoScroll", {
  overflow: "auto"
});
//# sourceMappingURL=CssUtils.js.map
